// pages/admin/AddBlogPage.tsx
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import Dropzone from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Col, FormGroup, Label, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import { createBlogAPI } from "../../../../../api/blogAPIs.ts"; // Adjust path
import { PreviewCard } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const AddBlogPage = () => {
    // const [categories, setCategories] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()
    // useEffect(() => {
    //     const fetchCategories = async () => {
    //         try {
    //             const response = await getAllCategories();
    //             setCategories(response.data.data);
    //         } catch (error) {
    //             console.error("Error fetching categories:", error);
    //             toast.error("Failed to fetch categories.");
    //         }
    //     };

    //     fetchCategories();
    // }, []);

    const categories = [
        { _id: "healthy-recipes", name: "Healthy Recipes & Nutrition" },
        { _id: "himalayan-lifestyle", name: "Himalayan Lifestyle & Superfoods" },
        { _id: "natural-wellness", name: "Natural Wellness & Ayurveda" },
        { _id: "product-education", name: "Product Education & Guides" },
        { _id: "pahadi-culture", name: "Pahadi Culture & Festivals" },
        { _id: "sustainable-living", name: "Sustainable Living" },
        { _id: "women-empowerment", name: "Women Empowerment Stories" },
        { _id: "news-announcements", name: "News & Announcements" },
    ];

    const handleDropChange = (acceptedFiles, setFieldValue) => {
        setFiles(
            acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) }))
        );
        setFieldValue("imageUrl", acceptedFiles);
    };

    const generateSlug = (title) => {
        if (!title) return "";
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")
            .substring(0, 50);
    };

    const initialValues = {
        title: "",
        content: "",
        category: "",
        sequence: 0,
        isPinned: false,
        isHidden: false,
        tags: "",
        imageUrl: null,
    };

    const validationSchema = Yup.object({
        title: Yup.string().required("Blog title is required"),
        content: Yup.string()
            .required("Content is required")
            .test("is-empty", "Content is required", (value) => {
                const strippedValue = value.replace(/<[^>]+>/g, "").trim();
                return strippedValue.length > 0;
            }),
        category: Yup.string().required("Category is required"),
        sequence: Yup.number()
            .required("Sequence is required")
            .integer("Sequence must be an integer")
            .min(0, "Sequence cannot be negative"),
        tags: Yup.string(),
        imageUrl: Yup.mixed()
            .test("fileType", "Only .jpeg, .png, .jpg are accepted", (value) =>
                !value || value.every((file) => ["image/jpeg", "image/png", "image/jpg"].includes(file.type))
            ),
    });

    const handleSubmit = async (values, { resetForm }) => {
        setLoading(true);
        try {
            const formData = new FormData();
            const blogData = {
                ...values,
                tags: values.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag),
            };
            formData.append("data", JSON.stringify(blogData));
            if (files.length) {
                files.forEach((file) => formData.append("images", file));
            }

            const response = await createBlogAPI({ formData });
            if (!response.data.success) throw new Error(response.data.message);

            toast.success("Blog added successfully");
            resetForm();
            setFiles([]);
            navigate("/blogs");
        } catch (error) {
            console.error("Error adding blog:", error);
            toast.error(error.message || "Failed to add blog");
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    };

    const formats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "link", "image"];

    return (
        <React.Fragment>
            <Head title="Add Blog" />
            <Content className="container mt-5">
                <PreviewCard>
                    <h3>Add Blog</h3>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ setFieldValue, values }) => (
                            <Form>
                                <Row className="g-3">
                                    <Col sm="12">
                                        <Label>Blog Title</Label>
                                        <Field
                                            name="title"
                                            className="form-control"
                                            placeholder="Enter blog title"
                                        />
                                        <ErrorMessage name="title" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="12">
                                        <Label>Content</Label>
                                        <ReactQuill
                                            theme="snow"
                                            value={values.content}
                                            onChange={(value) => setFieldValue("content", value)}
                                            modules={modules}
                                            formats={formats}
                                            placeholder="Enter blog content"
                                            className={`${!values.content ? "is-invalid" : ""}`}
                                        />
                                        <ErrorMessage name="content" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="6">
                                        <Label>Category</Label>
                                        <Field as="select" name="category" className="form-control">
                                            <option value="">Select Category</option>
                                            {categories.map((category) => (
                                                <option key={category._id} value={category._id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </Field>
                                        <ErrorMessage name="category" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="6">
                                        <Label>Sequence</Label>
                                        <Field
                                            name="sequence"
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter sequence"
                                        />
                                        <ErrorMessage name="sequence" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="12">
                                        <FormGroup check className="mb-3">
                                            <Label check>
                                                <Field
                                                    type="checkbox"
                                                    name="isPinned"
                                                    className="form-check-input"
                                                />{" "}
                                                Pin Blog
                                            </Label>
                                        </FormGroup>
                                        <FormGroup check className="mb-3">
                                            <Label check>
                                                <Field
                                                    type="checkbox"
                                                    name="isHidden"
                                                    className="form-check-input"
                                                />{" "}
                                                Hide Blog
                                            </Label>
                                        </FormGroup>
                                    </Col>

                                    <Col sm="12">
                                        <Label>Tags (comma-separated)</Label>
                                        <Field
                                            name="tags"
                                            className="form-control"
                                            placeholder="Enter tags (e.g., tech, news)"
                                        />
                                        <ErrorMessage name="tags" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="12">
                                        <Label>Image</Label>
                                        <Dropzone
                                            onDrop={(acceptedFiles) => handleDropChange(acceptedFiles, setFieldValue)}
                                            accept="image/jpeg, image/png, image/jpg"
                                            maxFiles={1}
                                        >
                                            {({ getRootProps, getInputProps, isDragActive }) => (
                                                <section>
                                                    <div
                                                        {...getRootProps()}
                                                        className={`dropzone p-3 border-2 ${isDragActive ? "border-primary" : "border-dashed"} rounded`}
                                                    >
                                                        <input {...getInputProps()} />
                                                        <p className="text-muted text-center">
                                                            Drag 'n' drop an image here, or click to select
                                                        </p>
                                                        <p className="text-center text-secondary">
                                                            Accepted formats: .jpeg, .png, .jpg | Max 1 file
                                                        </p>
                                                    </div>
                                                </section>
                                            )}
                                        </Dropzone>
                                        <div className="d-flex flex-wrap mt-3">
                                            {files.map((file, index) => (
                                                <div
                                                    key={file.name}
                                                    className="position-relative m-2"
                                                    style={{ width: 100, height: 100 }}
                                                >
                                                    <img
                                                        src={file.preview}
                                                        alt="preview"
                                                        className="rounded img-fluid border"
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        className="position-absolute top-0 end-0"
                                                        onClick={() => {
                                                            const newFiles = [...files];
                                                            newFiles.splice(index, 1);
                                                            setFiles(newFiles);
                                                            setFieldValue("imageUrl", newFiles);
                                                        }}
                                                    >
                                                        ×
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                        <ErrorMessage name="imageUrl" component="div" className="text-danger" />
                                    </Col>

                                    <Col sm="12">
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                color="primary"
                                                type="submit"
                                                disabled={loading}
                                            >
                                                {loading ? <Spinner size="sm" /> : "Add Blog"}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Formik>
                </PreviewCard>
            </Content>
        </React.Fragment>
    );
};

export default AddBlogPage;